import React, { useState, useEffect } from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import { BillTypeEnum } from './BillType.enum';
import ExpenseDatePicker from './ExpenseDatePicker';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { COLORS } from '../../../utils/Application_Constants';
import eventBus from './EventBus';

const ExpenseDateRange = (props) => {
    const {
        index,
        expense,
        mode,
        isPosted,
        isPendingSubmission,
        isViewOnly,
        enableEditByCorrection = false,
    } = props;
    const { control, errors, watch, setValue } = useFormContext();
    const billType = watch(`expenseEntries[${index}].billType`);
    const isClinicianDeduction = billType === 'ClinicianDeduction';
    const [required, setRequired] = useState({ required: true });
    const nonBillable =
        billType &&
        (billType === BillTypeEnum.NonBillTeam || billType === BillTypeEnum.NonBillProducer);
    const startDate = watch(`expenseEntries[${index}].startDate`);
    const endDate = watch(`expenseEntries[${index}].endDate`);
    const minStartDate = nonBillable
        ? moment(new Date()).startOf('day').subtract(1, 'year')
        : moment(new Date()).startOf('day').subtract(6, 'months');
    const twoMonthsOut = moment(new Date()).startOf('day').add(60, 'days');
    const maxStartDate = endDate
        ? moment(endDate).isSameOrBefore(twoMonthsOut)
            ? moment(endDate)
            : twoMonthsOut
        : twoMonthsOut;
    const minEndDate = startDate
        ? moment(startDate)
        : moment(new Date()).startOf('day').subtract(6, 'months');
    const maxEndDate = moment(new Date()).startOf('day').add(6, 'months');

    const handleDateChange = (e, field) => {
        if (e && e.startOf('day')) {
            setValue(`expenseEntries[${index}].${field}`, e.startOf('day').format('YYYY-MM-DD'));
            if (field === 'startDate') {
                eventBus.emit(`startDateChanged${index}`, {
                    data: e.startOf('day').format('YYYY-MM-DD'),
                });
            }
        } else {
            setValue(`expenseEntries[${index}].${field}`, e);
            if (field === 'startDate') {
                eventBus.emit(`startDateChanged${index}`, { data: e });
            }
        }
    };

    useEffect(() => {
        const aDate = expense.startDate
            ? moment(expense.startDate).startOf('day').format('YYYY-MM-DD')
            : null;
        eventBus.emit(`startDateChanged${index}`, { data: aDate });
        return () => {
            // Unmounted from view
            eventBus.emit(`startDateChanged${index}`, { data: null }); // Call function when component is removed
        };
    }, []);

    useEffect(() => {
        setRequired({
            required:
                expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                ((mode !== 'cc' && !isClinicianDeduction) ||
                    (mode === 'cc' && expense?.status === ExpenseStatusValuesEnum.Approved)),
        });
    }, [expense?.status]);

    return (
        <Grid2 item size={{ xs: 12 }}>
            <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].startDate`}
                        defaultValue={expense.startDate || null}
                        rules={
                            isPosted
                                ? {}
                                : {
                                      required: required.required
                                          ? (!billType && 'Bill type is required') ||
                                            'Start date required'
                                          : false,
                                      validate: {
                                          invalid: (value) =>
                                              !value || moment(value).isValid() || 'Invalid date',
                                          maxDate: (value) => {
                                              if (!value) return true;
                                              if (endDate && moment(value).isAfter(endDate)) {
                                                  return 'Start date must be before end date';
                                              }
                                              if (moment(value).isAfter(maxStartDate)) {
                                                  return 'Start date must be within 60 days';
                                              }
                                              return true;
                                          },
                                          minDate: (value) =>
                                              !value ||
                                              moment(value).isSameOrAfter(minStartDate) ||
                                              'Min date reached',
                                      },
                                  }
                        }
                        render={({ onChange, value }) => (
                            <ExpenseDatePicker
                                onChange={(e) => {
                                    onChange(e);
                                    handleDateChange(e, 'startDate');
                                }}
                                value={value ? moment(value) : null}
                                id={`start-date${index}`}
                                label="Start date"
                                {...required}
                                minDate={minStartDate}
                                maxDate={maxStartDate}
                                disabled={
                                    isViewOnly ||
                                    !billType ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission && !enableEditByCorrection)
                                }
                                parentTextFieldProps={{
                                    error: Boolean(errors.expenseEntries?.[index]?.startDate),
                                    helperText: errors.expenseEntries?.[index]?.startDate?.message,
                                }}
                                parentSx={{
                                    '& .MuiInputBase-root': {
                                        borderColor: !errors.expenseEntries?.[index]?.startDate
                                            ? 'default'
                                            : `${COLORS.LT_PEPPER} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].endDate`}
                        defaultValue={expense.endDate || null}
                        rules={
                            isPosted
                                ? {}
                                : {
                                      required: required.required
                                          ? (!billType && 'Bill type is required') ||
                                            'End date required'
                                          : false,
                                      validate: {
                                          invalid: (value) =>
                                              !value || moment(value).isValid() || 'Invalid date',
                                          maxDate: (value) =>
                                              !value ||
                                              moment(value).isSameOrBefore(maxEndDate) ||
                                              'Max date reached',
                                          minDate: (value) =>
                                              !value ||
                                              moment(value).isSameOrAfter(minEndDate) ||
                                              'End date must be after start date',
                                      },
                                  }
                        }
                        render={({ onChange, value }) => (
                            <ExpenseDatePicker
                                onChange={(e) => {
                                    onChange(e);
                                    handleDateChange(e, 'endDate');
                                }}
                                value={value ? moment(value) : null}
                                id={`end-date${index}`}
                                label="End date"
                                {...required}
                                minDate={minEndDate}
                                maxDate={maxEndDate}
                                disabled={
                                    isViewOnly ||
                                    !billType ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission && !enableEditByCorrection)
                                }
                                parentTextFieldProps={{
                                    error: Boolean(errors.expenseEntries?.[index]?.endDate),
                                    helperText: errors.expenseEntries?.[index]?.endDate?.message,
                                }}
                                parentSx={{
                                    '& .MuiInputBase-root': {
                                        borderColor: !errors.expenseEntries?.[index]?.endDate
                                            ? 'default'
                                            : `${COLORS.LT_PEPPER} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid2>
            </Grid2>
        </Grid2>
    );
};

export default ExpenseDateRange;
